import { BaseService } from './BaseService';

export class DashboardService extends BaseService {
    async getResidenceCombinations(offset = 0, skip = 0, limit = 10, sortField = 'id', sortOrder = 1, filters) {
        let newFilter = this.getFilters(filters);
        
        let parameters = {
            "offset": offset,
            "limit": limit,
            "skip": skip,
            "order": `${sortField} ${sortOrder == 1 ? 'desc' : 'asc'}`,
        };

        if (newFilter) {
            parameters["where"] = newFilter;
        }

        return this.get(`/residences/combinations?filter=${encodeURI(JSON.stringify(parameters))}`, true);
    }

    getFilters(filters = {}) {
        let newFilter = null;

        const fromTo = {
            'contains': 'like',
            'equals': 'eq'
        };

        if (filters) {
            for (const [key, value] of Object.entries(filters)) {
                if (value.constraints[0].value != null) {
                    let newValue = {};

                    let operator = fromTo[value.constraints[0].matchMode];

                    if (operator == 'like') {
                        newValue[operator] = `%${value.constraints[0].value}%`;
                    } else {
                        newValue[operator] = value.constraints[0].value;
                    }

                    if (newFilter == null) {
                        newFilter = {}
                    }

                    newFilter[key] = newValue;
                }
            }
        }
        return newFilter;
    }

    async getModuleQuestionsCount() {
        // ModuleQuestionCount[]
        return this.get('/statistics/modules/questions-count', true);
    }

    async getResidenceCombinations(activeOnly) {
        // ResidenceCombination[]
        const params = this.buildQueryString({ activeOnly });
        return this.get(`/statistics/residences/combinations${params}`, true);
    }

    async getMedicalServicesUserCount(activeOnly, from, to) {
        // MedicalServiceUserCount[]
        const params = this.buildQueryString({ activeOnly, from, to });
        return this.get(`/statistics/medical-services/users-count${params}`, true)
            .then(res => res.map(item => ({
                medicalServiceId: item.medicalServiceId,
                medicalServiceName: item.medicalServiceName,
                state: item.state,
                userCount: parseInt(item.userCount)
            })));
    }

    async getActiveUsersNow(minutesThreshold) {
        // { activeUserCount: number } — aproximação (sync do app é
        // offline-first em lote, não presença em tempo real).
        const params = this.buildQueryString({ minutesThreshold });
        return this.get(`/sessions/analytics/active-now${params}`, true);
    }

    async getActiveUsersHourly(hours) {
        // { hour, activeUserCount }[] — histórico persistido (reconstruído
        // do AppSession), diferente do getActiveUsersNow (só o instante atual).
        const params = this.buildQueryString({ hours });
        return this.get(`/sessions/analytics/active-hourly${params}`, true);
    }

    async getChurnCandidates(inactiveDays) {
        // ChurnCandidate[] — mesma lista usada pelo worker de notificação.
        const params = this.buildQueryString({ inactiveDays });
        return this.get(`/sessions/analytics/churn-candidates${params}`, true);
    }

    async getSessionsDailySummary(days) {
        // { date, userCount, avgDurationSeconds }[] — userCount é usuários
        // distintos com sessão naquele dia, não nº de sessões abertas.
        const params = this.buildQueryString({ days });
        return this.get(`/sessions/analytics/daily-summary${params}`, true);
    }

    async getSubscriptionsExpiringSoonCountDaily(days, aheadDays) {
        // { date, count }[]
        const params = this.buildQueryString({ days, aheadDays });
        return this.get(`/subscriptions/expiring-soon-count-daily${params}`, true);
    }

    async getSubscriptionsActiveCountDaily(days) {
        // { date, count }[]
        const params = this.buildQueryString({ days });
        return this.get(`/subscriptions/active-count-daily${params}`, true);
    }

    async getMedicalServicesByState() {
        // MedicalServiceByState[]
        return this.get('/statistics/medical-services/by-state', true)
            .then(res => res.map(item => ({
                state: item.state,
                serviceCount: parseInt(item.serviceCount)
            })));
    }

    async getTestQuestionsCount() {
        // TestQuestionCount[]
        return this.get('/statistics/tests/questions-count', true)
            .then(res => res.map(item => ({
                testId: item.testId,
                testTitle: item.testTitle,
                questionCount: parseInt(item.questionCount)
            })));
    }

    async getImportationsTimeline() {
        // ImportationTimeline[]
        return this.get('/statistics/importations/timeline', true)
            .then(res => res.map(item => ({
                date: item.date,
                fileName: item.fileName,
                status: item.status,
                errorCount: parseInt(item.errorCount)
            })));
    }

    async getDailyNewUsers() {
        // DailyNewUsers[]
        return this.get('/statistics/users/daily-new', true)
            .then(res => res.map(item => ({
                date: item.date,
                newUsers: parseInt(item.newUsers),
                // Renovação, upgrade ou recompra (backend antigo não envia: 0)
                additionalSubscriptions: parseInt(item.additionalSubscriptions ?? 0)
            })));
    }

    async getTestsForFilter() {
        return this.get('/dashboard/tests', true);
    }

    async getPerformanceByModule({ from, to, serviceId, uf, activeOnly } = {}) {
        const params = this.buildQueryString({ from, to, serviceId, uf, activeOnly });
        return this.get(`/dashboard/performance/by-module${params}`, true);
    }

    // userId opcional: sem ele, agrega a turma toda (heatmap da tela de
    // Desempenho); com ele, restringe a um único aluno (heatmap acessado
    // pela coluna Detalhamento do Ranking de Alunos).
    async getModulePerformanceHeatmap({ from, to, serviceId, uf, userId, moduleId, activeOnly } = {}) {
        const params = this.buildQueryString({ from, to, serviceId, uf, userId, moduleId, activeOnly });
        return this.get(`/dashboard/performance/by-module-heatmap${params}`, true);
    }

    async getPerformanceByTest({ from, to, serviceId, uf, activeOnly } = {}) {
        const params = this.buildQueryString({ from, to, serviceId, uf, activeOnly });
        return this.get(`/dashboard/performance/by-test${params}`, true);
    }

    // Mesma ideia do getModulePerformanceHeatmap, agrupado por prova.
    async getTestPerformanceHeatmap({ from, to, serviceId, uf, userId, testId, activeOnly } = {}) {
        const params = this.buildQueryString({ from, to, serviceId, uf, userId, testId, activeOnly });
        return this.get(`/dashboard/performance/by-test-heatmap${params}`, true);
    }

    async getPerformanceByQuestion({ moduleId, testId, questionId, from, to, limit, serviceId, uf, activeOnly } = {}) {
        const params = this.buildQueryString({ moduleId, testId, questionId, from, to, limit, serviceId, uf, activeOnly });
        return this.get(`/dashboard/performance/by-question${params}`, true);
    }

    async getQuestionDetail(id, { moduleId, testId, from, to, serviceId, uf } = {}) {
        const params = this.buildQueryString({ moduleId, testId, from, to, serviceId, uf });
        return this.get(`/dashboard/performance/question/${id}${params}`, true);
    }

    async getStudentsRanking({ moduleId, search, offset, limit, sortField, sortOrder, activeOnly, startDate, endDate } = {}) {
        const params = this.buildQueryString({ moduleId, search, offset, limit, sortField, sortOrder, activeOnly, startDate, endDate });
        return this.get(`/dashboard/performance/students-ranking${params}`, true);
    }

    async getStudentTimeline(userId, { offset, limit } = {}) {
        const params = this.buildQueryString({ offset, limit });
        return this.get(`/dashboard/students/${userId}/timeline${params}`, true);
    }

    async getQuizAttemptDetail(userId, { moduleId, testId } = {}) {
        const params = this.buildQueryString({ moduleId, testId });
        return this.get(`/dashboard/students/${userId}/quiz-attempt${params}`, true);
    }

    async getPracticeTestAttemptDetail(userId, uuid) {
        return this.get(`/dashboard/students/${userId}/practice-test-attempt/${uuid}`, true);
    }

    // Questões respondidas por um aluno numa célula específica do heatmap
    // (módulo ou prova + semana) — usado no clique da célula.
    async getHeatmapCellDetail(userId, { moduleId, testId, period, source } = {}) {
        const params = this.buildQueryString({ moduleId, testId, period, source });
        return this.get(`/dashboard/students/${userId}/heatmap-cell-detail${params}`, true);
    }

    async getStudentMirror(userId) {
        return this.get(`/dashboard/students/${userId}/mirror`, true);
    }

    async getStudentQuizModules(userId) {
        return this.get(`/dashboard/students/${userId}/quiz-modules`, true);
    }

    async getStudentQuizTests(userId) {
        return this.get(`/dashboard/students/${userId}/quiz-tests`, true);
    }

    buildQueryString(params = {}) {
        const query = Object.entries(params)
            .filter(([, value]) => value !== undefined && value !== null && value !== '')
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
            .join('&');

        return query ? `?${query}` : '';
    }
}